import React, { useState, useEffect } from 'react';

import styled from '@emotion/styled';
import { getLocalStorageItem } from '@revenuemarket/browser';
import { Button, typographyStyle } from '@revenuemarket/cds';
import { colors, PrimaryColors } from '@revenuemarket/design';
import { Typography } from 'antd';
import { aiFormattedPageContext } from '~hooks/prompt/aiFormatter';
import { useCompanyId } from '~hooks/useCompanyId';
import useLayoutStore from '~store/layoutStore';

import ChatInterface from './ChatInterface';
import ConversationManager from './ConversationManager';

interface AIChatSidebarProps {}

export default function AIChatSidebar({}: AIChatSidebarProps) {
  const { isAiChatSidebarOpen, setIsAiChatSidebarOpen } = useLayoutStore();
  const [aiPromptFeature, setAiPromptFeature] = useState<boolean>(false);
  const [conversationId, setConversationId] = useState<string | undefined>(undefined);

  const companyId = useCompanyId()?.companyId;

  // 클라이언트 side에서 feature 확인
  useEffect(() => {
    const feature = getLocalStorageItem('aiPromptFeature');
    setAiPromptFeature(!!feature);
  }, []);

  // 사이드바가 열릴 때 페이지 컨텍스트 로깅
  useEffect(() => {
    if (isAiChatSidebarOpen) {
      console.log(aiFormattedPageContext().context);
    }
  }, [isAiChatSidebarOpen]);

  if (!aiPromptFeature) {
    return null;
  }

  const toggleSidebar = () => {
    setIsAiChatSidebarOpen(!isAiChatSidebarOpen);
  };

  const handleConversationChange = (newConversationId: string) => {
    setConversationId(newConversationId);
  };

  return (
    <>
      <ToggleButton isOpen={isAiChatSidebarOpen} onClick={toggleSidebar}>
        {isAiChatSidebarOpen ? '✕' : '💬'}
      </ToggleButton>

      <SidebarContainer isOpen={isAiChatSidebarOpen}>
        <Header>
          <Typography.Text style={typographyStyle['18_600']}>AI Assistant</Typography.Text>
          <div style={{ display: 'flex', gap: '8px' }}>
            <ConversationManager
              companyId={companyId}
              isOpen={isAiChatSidebarOpen}
              conversationId={conversationId}
              onConversationChange={handleConversationChange}
            />
            <Button
              buttonType="default"
              onClick={toggleSidebar}
              style={{ padding: '4px', minWidth: 'auto' }}
            >
              ✕
            </Button>
          </div>
        </Header>

        <ChatInterface conversationId={conversationId} companyId={companyId} />
      </SidebarContainer>
    </>
  );
}

const SidebarContainer = styled.div<{ isOpen: boolean }>`
  width: ${({ isOpen }) => (isOpen ? '400px' : '0')};
  min-width: ${({ isOpen }) => (isOpen ? '400px' : '0')};
  height: 100vh;
  background: ${colors.grayscaleWhite};
  border-left: 1px solid ${colors.grayscale200};
  transition: width 0.3s ease;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  overflow: hidden;
`;

const Header = styled.div`
  padding: 16px;
  border-bottom: 1px solid ${colors.grayscale200};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ToggleButton = styled.button<{ isOpen: boolean }>`
  position: fixed;
  top: 50%;
  right: ${({ isOpen }) => (isOpen ? '400px' : '0')};
  transform: translateY(-50%);
  width: 48px;
  height: 48px;
  background: ${PrimaryColors.primary500};
  color: white;
  border: none;
  border-radius: 8px 0 0 8px;
  cursor: pointer;
  transition: right 0.3s ease;
  z-index: 1001;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
`;
